import { findNearByStationV2 } from "@/api/configPage";
import StationItem from "@/component/station-item";
import { useConfigPage } from "@/hooks/useConfigPage/useConfigPage";
import { NativeByUrlList } from "@/utils/common";
import { CHARGE_CDN } from "@/utils/constants";
import { trackGdp } from "@/utils/gio-report";
import { processOssImage } from "@/utils/image";
import { getBrowserEnv, getChannel } from "@/utils/tools";
import { Toast } from "@xp-design/mobile";
import { InfiniteScroll, List } from "antd-mobile";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { enhancedSafeChainFind } from "../../utils";
import "./index.less";

const sortOptions = [
  { value: 1, label: "综合排序" },
  { value: 2, label: "距离优先" },
];

interface DynamicStationProps {
  className?: string;
  limitNum?: number; // 可见场站数量限制
  activityInfo?: any; // 组件信息
}

interface StationListItem {
  stationId: number;
  stationName: string;
  stationAddr: string;
  distance: number;
  stationAvailabilityDesc: string;
  stationLabelList: any[];
  stationBillingInfo: any;
  stationActivityInfoV5: any;
  stationEquipOverViewList: any[];
}

// 真实的附近充电站列表接口
const QueryStationList = async (params: {
  longitude: number;
  latitude: number;
  sortType: number;
  pageNo: number;
  pageSize: number;
  radius?: number;
  filterCriteriaCodeList?: any[];
  currentLon?: number;
  currentLat?: number;
}): Promise<{
  code: number;
  data: { result: StationListItem[]; total: number };
}> => {
  try {
    const response = await findNearByStationV2({
      ...params,
      source: 1,
    });

    console.log("findNearByStationV2 response:", response);

    return {
      code: response.code || 200,
      data: {
        // 根据接口返回的数据结构，result 字段直接就是站点列表数组
        result: response.data?.result || [],
        // 使用返回数据的长度作为 total，如果返回的数据小于 pageSize，说明没有更多数据了
        total: response.data?.result?.length || 0,
      },
    };
  } catch (error) {
    console.error("QueryStationList API error:", error);
    throw error;
  }
};

const DynamicStation: React.FC<
  DynamicStationProps & { navHeight?: number }
> = ({ className = "", activityInfo, navHeight = 0 }) => {
  const [data, setData] = useState<StationListItem[]>([]);
  const [hasMore, setHasMore] = useState(false); // 初始为 false，防止 InfiniteScroll 自动触发
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);
  const [isComponentVisible, setIsComponentVisible] = useState(true);

  // 从上层获取定位信息
  const {
    location: contextLocation,
    locationStatus,
    refetchLocation,
  } = useConfigPage();
  const isGettingLocation = locationStatus === "loading";
  const isUseDefault = contextLocation?.isUseDefault || false;
  const address = contextLocation?.address || "";
  const location = contextLocation
    ? { lng: contextLocation.lng, lat: contextLocation.lat }
    : null;

  const locationImg = processOssImage(
    `${CHARGE_CDN}/chargeVip/location.png`,
  ) as unknown as string;

  const arrowUpImg = processOssImage(
    `${CHARGE_CDN}/common/arrow_up.png`,
  ) as unknown as string;

  const dynamicStationBase = useMemo(() => {
    return (
      enhancedSafeChainFind(activityInfo.moduleJsonData)?.base(
        "type",
        "dynamicStation",
      )?.val || {}
    );
  }, [activityInfo.moduleJsonData]);

  const isLimit = useMemo(() => {
    return (
      dynamicStationBase?.isLimit ||
      activityInfo?.moduleJsonData?.base?.[0]?.isLimit
    );
  }, [activityInfo.moduleJsonData, dynamicStationBase]);

  const limitNum = useMemo(() => {
    return (
      dynamicStationBase.visibleCount ||
      activityInfo?.moduleJsonData?.base?.[0]?.visibleCount
    );
  }, [activityInfo.moduleJsonData, dynamicStationBase]);

  const radius = useMemo(() => {
    return (
      dynamicStationBase?.visibleRange ||
      activityInfo?.moduleJsonData?.base?.[0]?.visibleRange
    );
  }, [activityInfo.moduleJsonData, dynamicStationBase]);

  const jumpLinkList = useMemo(() => {
    return (
      dynamicStationBase?.jumpUrlMap ||
      activityInfo?.moduleJsonData?.base?.[0]?.jumpUrlMap
    );
  }, [activityInfo.moduleJsonData, dynamicStationBase]);

  const bgUrl = useMemo(() => {
    const result = enhancedSafeChainFind(activityInfo.moduleJsonData)?.base(
      "bgImg",
    );
    return result?.val?.val || activityInfo?.moduleJsonData?.base?.[1]?.val;
  }, [activityInfo.moduleJsonData, dynamicStationBase]);

  const sortOrder = useMemo(() => {
    return dynamicStationBase?.sortOrder;
  }, [activityInfo.moduleJsonData, dynamicStationBase]);

  const [sortType, setSortType] = useState<1 | 2>(sortOrder || 2);

  const pageSize = isLimit && limitNum ? Math.min(limitNum, 5) : 5; // 动态计算 pageSize
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const componentObserverRef = useRef<IntersectionObserver | null>(null);
  const headerObserverRef = useRef<IntersectionObserver | null>(null);
  // 记录上次请求的定位，用于判断是否需要重新请求
  const lastLocationRef = useRef<{ lng: number; lat: number } | null>(null);

  const { isXPApp } = getBrowserEnv();

  // 使用 useMemo 缓存解析结果，确保在 activityInfo 更新后重新计算
  const filterCriteriaCodeList = useMemo(() => {
    try {
      const dataJson = activityInfo.dataJson
        ? JSON.parse(activityInfo.dataJson)
        : {};
      return dataJson.filterCriteriaCodeList || [];
    } catch (error) {
      console.error("解析 filterCriteriaCodeList 失败:", error);
      return [];
    }
  }, [activityInfo.dataJson, activityInfo.dataJsonData]); // 依赖两者，确保任一变化都重新计算

  /**
   * 重新获取定位（点击重新开启按钮）
   */
  const handleRefetchLocation = async (isFirst = true) => {
    if (refetchLocation) {
      const result = await refetchLocation();
      if (result?.isUseDefault && !isFirst) {
        Toast.show({ content: "获取定位失败，请稍后重试", duration: 2000 });
      }
    }
  };

  // 使用 useCallback 封装获取充电站列表数据的方法
  const fetchStations = useCallback(
    async (
      lng: number,
      lat: number,
      pageNum: number,
      currentSortType?: number,
    ): Promise<{ data: StationListItem[]; hasMore: boolean }> => {
      const res = await QueryStationList({
        longitude: lng,
        latitude: lat,
        sortType: currentSortType || sortType,
        pageNo: pageNum,
        pageSize: pageSize,
        radius: radius,
        filterCriteriaCodeList: filterCriteriaCodeList, // 依赖 useMemo 的值
        currentLon: lng,
        currentLat: lat,
      });

      if (res.code === 200) {
        const newData = res.data.result;
        const hasMore = newData.length === pageSize;

        console.log(
          `页面 ${pageNum}: 加载了 ${newData.length} 条数据 (限制: ${isLimit && limitNum ? limitNum : "无限制"}), 还有更多: ${hasMore}`,
        );

        return { data: newData, hasMore };
      } else {
        throw new Error(`获取充电站列表失败: ${res.code}`);
      }
    },
    [sortType, pageSize, radius, filterCriteriaCodeList], // 依赖项：当这些值变化时，函数会重新创建
  );

  // 定位变化时重新请求站点数据
  useEffect(() => {
    if (!location) {
      return;
    }

    // 检查定位是否变化
    const lastLoc = lastLocationRef.current;
    if (
      lastLoc &&
      lastLoc.lng === location.lng &&
      lastLoc.lat === location.lat
    ) {
      return;
    }

    // 更新记录
    lastLocationRef.current = { lng: location.lng, lat: location.lat };

    // 重置状态并加载首页数据
    setLoading(true);
    setData([]);

    fetchStations(location.lng, location.lat, 1)
      .then((result) => {
        let newData = result.data;
        if (isLimit && limitNum) {
          newData = newData.slice(0, limitNum);
        }
        setData(newData);
        setPage(2);
        setHasMore(
          result.hasMore &&
            (!isLimit || !limitNum || newData.length < limitNum),
        );
      })
      .catch((err: any) => {
        Toast.show({ content: err.message || "加载失败", duration: 2000 });
        setHasMore(false);
      })
      .finally(() => setLoading(false));
  }, [location, isUseDefault, filterCriteriaCodeList, fetchStations]); // filterCriteriaCodeList 变化时重新请求，fetchStations 确保使用最新函数

  useEffect(() => {
    console.log("navHeight", navHeight);
  }, [navHeight]);

  // 设置滚动监听
  useEffect(() => {
    const setupObservers = () => {
      // 监听整个组件是否在视口中
      componentObserverRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setIsComponentVisible(entry.isIntersecting);
            // 如果组件完全离开视口，隐藏固定头部
            if (!entry.isIntersecting) {
              setIsHeaderFixed(false);
            }
          });
        },
        {
          threshold: [0, 0.1], // 当组件刚进入或完全离开视口时触发
          rootMargin: "-10px 0px 0px 0px", // 稍微延迟触发
        },
      );

      // 监听头部是否到达视口顶部
      headerObserverRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // 当头部元素离开视口顶部时，显示固定头部
            const top = isXPApp ? 50 : 0;
            console.log(
              "header entry",
              entry.boundingClientRect.top,
              isComponentVisible,
            );
            if (
              !entry.isIntersecting &&
              entry.boundingClientRect.top < top &&
              isComponentVisible
            ) {
              setIsHeaderFixed(true);
            } else if (entry.isIntersecting) {
              setIsHeaderFixed(false);
            }
          });
        },
        {
          threshold: [0, 1],
          rootMargin: "0px 0px 0px 0px",
        },
      );

      // 开始观察
      if (containerRef.current) {
        componentObserverRef.current.observe(containerRef.current);
      }
      if (headerRef.current) {
        headerObserverRef.current.observe(headerRef.current);
      }
    };

    // 延迟设置观察器，确保DOM已经渲染
    const timer = setTimeout(setupObservers, 100);

    return () => {
      clearTimeout(timer);
      componentObserverRef.current?.disconnect();
      headerObserverRef.current?.disconnect();
    };
  }, [isComponentVisible]);

  // 加载更多数据
  const loadMore = async (resetData = false, currentSortType?: number) => {
    if (loading || (!hasMore && !resetData) || !location) return;

    // 如果设置了 isLimit 且 limitNum 存在，检查是否已经达到限制
    if (isLimit && limitNum && !resetData && data.length >= limitNum) {
      setHasMore(false);
      return;
    }

    setLoading(true);
    try {
      const currentPage = resetData ? 1 : page;
      const result = await fetchStations(
        location.lng,
        location.lat,
        currentPage,
        currentSortType,
      );

      let newData = result.data;

      // 如果设置了 isLimit 且 limitNum 存在，确保不超过限制
      if (isLimit && limitNum) {
        if (resetData) {
          newData = newData.slice(0, limitNum);
        } else {
          const remainingSlots = limitNum - data.length;
          newData = newData.slice(0, remainingSlots);
        }
      }

      if (resetData) {
        setData(newData);
        setPage(2);
      } else {
        setData((prevData) => [...prevData, ...newData]);
        setPage((prevPage) => prevPage + 1);
      }

      // 重新计算是否还有更多数据
      let stillHasMore = result.hasMore;

      if (isLimit && limitNum) {
        const currentTotalLoaded = resetData
          ? newData.length
          : data.length + newData.length;
        if (currentTotalLoaded >= limitNum) {
          stillHasMore = currentTotalLoaded === limitNum;
        }
      }

      setHasMore(stillHasMore);
    } catch (err: any) {
      Toast.show({
        content: err.message || "加载失败",
        duration: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  // 处理排序切换
  const handleSortChange = (newSortType: 1 | 2) => {
    trackGdp("P20189", "B004", {
      trackName: "充电_营销专题页_组件点击",
      origin: getChannel(),
      userType: "",
      versionId: activityInfo.versionCode,
      pageId: activityInfo.pageCode,
      ComponentCode: activityInfo.moduleId,
      type: activityInfo.moduleType,
      comp_style: JSON.parse(activityInfo.moduleJson).visual?.visualData[0]
        ?.typeName,
      clickLocation: newSortType === 1 ? "综合排序" : "距离优先",
    });

    if (newSortType === sortType) {
      setShowFilterMenu(false);
      return;
    }

    setSortType(newSortType);
    setShowFilterMenu(false);

    // 重置分页和数据状态
    setPage(1);
    setData([]);
    setHasMore(true);

    // 重新加载数据
    loadMore(true, newSortType);
  };

  // 渲染头部内容
  const renderHeader = (isFixed = false) => (
    <div className={`${isFixed ? "fixed-header" : ""}`}>
      {isUseDefault && (
        <div className="location-default-tip">
          <span className="desc">定位服务未开启，当前定位到小鹏汽车总部</span>
          <div className="btn" onClick={() => handleRefetchLocation(false)}>
            重新开启
          </div>
        </div>
      )}
      <div className="dynamic-station-header">
        <div className="location-info">
          {(address || isGettingLocation) && (
            <>
              <img className="icon" src={locationImg} alt="" />
              <span className="address">
                {isGettingLocation ? "正在获取位置信息..." : address}
              </span>
            </>
          )}
        </div>
        <div className="filter-section">
          <div
            className="filter-trigger"
            onClick={() => setShowFilterMenu(!showFilterMenu)}
          >
            <span className="filter-text">
              {sortOptions.find((opt) => opt.value === sortType)?.label}
            </span>
            <img
              className={`filter-arrow ${showFilterMenu ? "active" : ""}`}
              src={arrowUpImg}
              alt=""
            />
          </div>
          {showFilterMenu && (
            <div className="filter-menu">
              {sortOptions.map((option) => (
                <div
                  key={option.value}
                  className={`filter-option ${sortType === option.value ? "active" : ""}`}
                  onClick={() => handleSortChange(option.value as 1 | 2)}
                >
                  <span>{option.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // 渲染充电站项
  const renderStationItem = (station: StationListItem, index: number) => {
    const stationProps = {
      station,
      showDistance: true,
      withLine: index < data.length - 1,
      showAddress: true,
      showAct: true,
      showTab: true,
      from: "dynamicStation",
    };

    const handleItemClick = () => {
      trackGdp("P20189", "B004", {
        trackName: "充电_营销专题页_组件点击",
        origin: getChannel(),
        userType: "",
        versionId: activityInfo.versionCode,
        pageId: activityInfo.pageCode,
        ComponentCode: activityInfo.moduleId,
        type: activityInfo.moduleType,
        comp_style: JSON.parse(activityInfo.moduleJson).visual?.visualData[0]
          ?.typeName,
        clickLocation: station.stationId,
      });
    };

    return (
      <div
        key={station.stationId}
        className="dynamic-station-item"
        onClick={handleItemClick}
      >
        <StationItem {...stationProps} />
      </div>
    );
  };

  return (
    <div
      className={`dynamic-station-container ${className}`}
      ref={containerRef}
      style={{
        backgroundColor: bgUrl ? "#ffffff" : "transparent",
        backgroundImage: bgUrl ? `url(${processOssImage(bgUrl)})` : "none",
        backgroundSize: "100% auto",
        backgroundPosition: "center top",
        backgroundRepeat: "repeat-y",
      }}
    >
      {/* 原始头部 */}
      <div ref={headerRef}>{renderHeader()}</div>

      {/* 固定头部 */}
      {isHeaderFixed && isComponentVisible && (
        <div
          className="fixed-header-container"
          style={{
            top: isXPApp ? `${navHeight}px` : "0",
            backgroundColor: bgUrl ? "#ffffff" : "transparent",
            backgroundImage: bgUrl ? `url(${processOssImage(bgUrl)})` : "none",
            backgroundSize: "100% auto",
            backgroundPosition: "center top",
            backgroundRepeat: "repeat-y",
          }}
        >
          {renderHeader(true)}
        </div>
      )}

      <div className="dynamic-station-list">
        <List>
          {data.map((station, index) => renderStationItem(station, index))}
          <InfiniteScroll
            loadMore={() => loadMore(false)}
            hasMore={hasMore}
            threshold={50}
          >
            {loading && (
              <div className="loading-container">
                <div className="loading-spinner" />
                <span>加载中...</span>
              </div>
            )}

            {!hasMore && data.length > 0 && (
              <div
                className="no-more-container"
                onClick={() => NativeByUrlList(jumpLinkList)}
              >
                <span>
                  {isLimit && limitNum && data.length >= limitNum
                    ? `查看更多场站`
                    : "没有更多数据了"}
                </span>
                {isLimit && limitNum && data.length >= limitNum ? (
                  <img
                    src={processOssImage(
                      "https://s.xiaopeng.com/xp-charge/react-native/pile/more_icon_v2.png",
                    )}
                    className={"moreIcon"}
                  ></img>
                ) : null}
              </div>
            )}

            {!loading && !location && (
              <div className="loading-container">
                <div className="loading-spinner" />
                <span>加载中...</span>
              </div>
            )}

            {!hasMore && data.length === 0 && !loading && location && (
              <div className="empty-container">
                <span>当前{Math.floor(radius / 1000)}公里内暂无场站</span>
              </div>
            )}
          </InfiniteScroll>
        </List>
      </div>

      {/* 点击遮罩关闭筛选菜单 */}
      {showFilterMenu && (
        <div
          className="filter-overlay"
          onClick={() => setShowFilterMenu(false)}
        />
      )}
    </div>
  );
};

export default DynamicStation;
